import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import API from '@/lib/api';

/* ════════════════════════════════════════════════════════════════════
   BillitPanel — Beheer Billit.be integratie (Belgisch boekhoud-SaaS)
   - Toggle aan/uit
   - API key + PartyID configureren
   - Test verbinding
   - Auto-push toggle
   - Customer import
   ════════════════════════════════════════════════════════════════════ */
export default function BillitPanel() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [partyId, setPartyId] = useState('');
  const [autoSend, setAutoSend] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [testing, setTesting] = useState(false);
  const [accountInfo, setAccountInfo] = useState(null);
  const [importing, setImporting] = useState(false);

  const load = async () => {
    try {
      const r = await API.get('/billit/config');
      setConfig(r.data);
      setApiKey(r.data.api_key_masked || '');
      setPartyId(r.data.party_id || '');
      setAutoSend(r.data.auto_send || false);
      setEnabled(r.data.enabled || false);
      setAccountInfo(r.data.account_info);
    } catch (e) {
      toast.error('Kon Billit configuratie niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const testConnection = async () => {
    if (!apiKey || apiKey.length < 8) {
      toast.error('Vul eerst een geldige API key in');
      return;
    }
    setTesting(true);
    try {
      const r = await API.post('/billit/test', { api_key: apiKey });
      const info = r.data;
      setPartyId(info.party_id || '');
      setAccountInfo({
        company_name: info.company_name,
        vat_number: info.vat_number,
        biller_id: info.biller_id,
        email: info.email,
      });
      toast.success(`✓ Verbonden met ${info.company_name || 'Billit'}`, {
        description: `PartyID: ${info.party_id} • VAT: ${info.vat_number}`,
        duration: 5000,
      });
    } catch (e) {
      toast.error('Verbinding mislukt: ' + (e.response?.data?.detail || e.message));
    } finally {
      setTesting(false);
    }
  };

  const saveConfig = async () => {
    if (!apiKey || !partyId) {
      toast.error('API key en PartyID zijn beide verplicht. Klik eerst op "Test verbinding".');
      return;
    }
    try {
      await API.put('/billit/config', {
        api_key: apiKey,
        party_id: partyId,
        enabled,
        auto_send: autoSend,
        account_info: accountInfo,
      });
      toast.success('✓ Billit configuratie opgeslagen');
      load();
    } catch (e) {
      toast.error('Opslaan mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Billit configuratie verwijderen?\n\nDit verbreekt de verbinding. Documenten die al verzonden werden naar Billit blijven daar wel staan.')) return;
    try {
      await API.delete('/billit/config');
      toast.success('Billit losgekoppeld');
      setApiKey(''); setPartyId(''); setAutoSend(false); setEnabled(false); setAccountInfo(null);
      load();
    } catch (e) {
      toast.error('Verwijderen mislukt');
    }
  };

  const importCustomers = async () => {
    const updateAddresses = window.confirm(
      'Importeer alle klanten uit Billit naar BillFac?\n\n' +
      '✅ Bestaande klanten worden NIET opnieuw geïmporteerd — enkel gematcht op:\n' +
      '   1. billit_party_id  2. VAT-nummer  3. Bedrijfsnaam  4. Email\n\n' +
      '📍 Adresgegevens uit Billit worden ook meegenomen (voor nieuwe én bestaande klanten met lege velden)\n\n' +
      '🔄 Dubbels binnen dezelfde import worden overgeslagen\n\n' +
      'Klik "OK" om adres-velden bij bestaande klanten OOK bij te werken (indien leeg).\n' +
      'Klik "Cancel" om alleen nieuwe klanten aan te maken zonder bestaande te wijzigen.'
    );
    setImporting(true);
    try {
      const r = await API.post('/billit/import-customers', { update_addresses: updateAddresses });
      const d = r.data || {};
      toast.success(`✓ Import voltooid`, {
        description: `${d.created} nieuw · ${d.matched} gematcht · ${d.addresses_updated} adres bijgewerkt · ${d.skipped_dubbel} dubbels overgeslagen (van ${d.total_billit} totaal)`,
        duration: 10000,
      });
      // Als er details zijn (verborgen alert voor details ≥100)
      if (d.details_truncated) {
        console.info('[billit-import] Details truncated to 100. Full list not shown.');
      }
    } catch (e) {
      toast.error('Import mislukt: ' + (e.response?.data?.detail || e.message));
    } finally {
      setImporting(false);
    }
  };

  if (loading) return <div className="text-xs text-gray-500 p-4">Laden…</div>;

  return (
    <div className="space-y-4" data-testid="billit-panel">
      {/* Header & uitleg */}
      <div className="p-3 bg-[#FEF3C7] border-2 border-[#F59E0B]/40 text-xs text-[#78350F]">
        <div className="font-bold text-sm mb-1">🇧🇪 Billit — Belgisch boekhoud-SaaS</div>
        <div>
          Stuur verkoopfacturen automatisch naar je Billit-account. Vanaf 1 januari 2026 verstuurt Billit ze ook automatisch via Peppol (e-invoicing) naar je B2B klanten. Boekhouder ziet alles centraal.
        </div>
      </div>

      {/* Status badge */}
      {config?.configured && (
        <div className="flex items-center gap-3 p-3 bg-white border-2 border-[#10B981]/40">
          <span className={`px-2 py-1 text-[10px] font-bold uppercase ${enabled ? 'bg-[#D1FAE5] text-[#065F46]' : 'bg-gray-200 text-gray-600'}`}>
            {enabled ? '● actief' : 'pauze'}
          </span>
          {accountInfo && (
            <div className="flex-1 text-xs">
              <div className="font-bold text-gray-800">{accountInfo.company_name}</div>
              <div className="text-gray-500 text-[10px]">
                VAT: {accountInfo.vat_number} · PartyID: {accountInfo.biller_id}
              </div>
            </div>
          )}
          <button
            type="button"
            className="px-2 py-1 bg-[#FEE2E2] text-[#DC2626] text-[10px] font-bold hover:bg-[#FCA5A5]"
            onClick={disconnect}
            data-testid="billit-disconnect"
            title="Loskoppelen"
          >
            Loskoppelen
          </button>
        </div>
      )}

      {/* Configuratie velden */}
      <div className="space-y-3 bg-white border border-gray-200 p-3">
        <div>
          <label className="form-label">API Key</label>
          <input
            type="text"
            className="field-input font-mono text-xs"
            placeholder="Plak hier je API key uit Billit → Settings → API"
            value={apiKey}
            onChange={(e) => { setApiKey(e.target.value); setAccountInfo(null); setPartyId(''); }}
            data-testid="billit-api-key-input"
            autoComplete="off"
          />
          <div className="text-[10px] text-gray-500 mt-1">
            Maak een key aan in <a href="https://my.billit.be/Settings" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">my.billit.be → Settings → API</a>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            className="px-4 py-2 bg-[#002FA7] text-white text-xs font-bold hover:bg-[#001E6E] disabled:opacity-50"
            onClick={testConnection}
            disabled={testing || !apiKey}
            data-testid="billit-test-btn"
          >
            {testing ? '⏳ Bezig…' : '🔌 Test verbinding'}
          </button>
          {partyId && (
            <div className="flex-1 text-xs px-3 py-2 bg-[#ECFDF5] border border-[#10B981]/40 text-[#065F46] flex items-center gap-2">
              <span className="font-bold">✓ PartyID: {partyId}</span>
              {accountInfo?.company_name && <span className="text-gray-600">— {accountInfo.company_name}</span>}
            </div>
          )}
        </div>

        {partyId && (
          <>
            <hr className="border-gray-200" />
            <label className="flex items-center gap-2 cursor-pointer text-xs">
              <input
                type="checkbox"
                className="w-4 h-4 accent-[#002FA7]"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                data-testid="billit-enable-toggle"
              />
              <span className="font-bold">Activeer Billit-integratie</span>
              <span className="text-gray-500">(aan/uit-schakelaar voor alle Billit-functionaliteit)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-xs">
              <input
                type="checkbox"
                className="w-4 h-4 accent-[#7C3AED]"
                checked={autoSend}
                onChange={(e) => setAutoSend(e.target.checked)}
                disabled={!enabled}
                data-testid="billit-autosend-toggle"
              />
              <span className="font-bold">Automatisch versturen bij status &quot;Verzonden&quot;</span>
              <span className="text-gray-500">(anders alleen handmatig via knop)</span>
            </label>

            <button
              type="button"
              className="w-full px-4 py-2 bg-[#059669] text-white text-sm font-bold hover:bg-[#047857]"
              onClick={saveConfig}
              data-testid="billit-save-btn"
            >
              💾 Opslaan
            </button>
          </>
        )}
      </div>

      {/* Acties (alleen tonen als geconfigureerd) */}
      {config?.configured && config?.enabled && (
        <div className="bg-gray-50 border border-gray-200 p-3 space-y-2">
          <div className="text-xs font-bold uppercase text-gray-600 tracking-wide">Acties</div>
          <button
            type="button"
            className="w-full px-3 py-2 bg-white border border-[#7C3AED] text-[#7C3AED] text-xs font-bold hover:bg-[#F5F3FF] disabled:opacity-50"
            onClick={importCustomers}
            disabled={importing}
            data-testid="billit-import-customers-btn"
          >
            {importing ? '⏳ Bezig…' : '⬇ Importeer klanten uit Billit'}
          </button>
          <div className="text-[10px] text-gray-500 leading-tight">
            Haalt alle klanten uit Billit op. Bestaande klanten in BillFac worden gematcht op BTW of naam — nieuwe worden aangemaakt. Max 5000 klanten per import.
          </div>
        </div>
      )}

      {/* Uitleg */}
      <details className="border border-gray-200 bg-gray-50/50">
        <summary className="cursor-pointer px-3 py-2 text-xs font-bold text-gray-700 hover:bg-gray-100 select-none">
          📖 Hoe gebruiken?
        </summary>
        <div className="px-4 py-3 space-y-2 text-xs text-gray-700">
          <div><strong>Stap 1.</strong> Log in op <a href="https://my.billit.be" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">my.billit.be</a> en ga naar <em>Settings → API</em>. Klik <em>&quot;Generate API Key&quot;</em>.</div>
          <div><strong>Stap 2.</strong> Plak de key hierboven en klik <em>&quot;Test verbinding&quot;</em>. PartyID wordt automatisch ingevuld.</div>
          <div><strong>Stap 3.</strong> Vink <em>&quot;Activeer Billit-integratie&quot;</em> aan en klik <em>&quot;Opslaan&quot;</em>.</div>
          <div><strong>Stap 4.</strong> Optioneel: importeer bestaande klanten uit Billit zodat ze niet dubbel worden aangemaakt bij de eerste push.</div>
          <div><strong>Stap 5.</strong> Op een factuur klik je nu op <em>&quot;→ Naar Billit&quot;</em> (rechtsboven). De factuur verschijnt in je Billit-account binnen 2 seconden. Vanaf 1/1/2026 wordt hij ook automatisch via Peppol naar de klant verstuurd.</div>
          <div className="p-2 bg-[#FEF3C7] border border-[#F59E0B]/40 text-[11px]">
            💡 <strong>Pro-tip:</strong> activeer <em>&quot;Automatisch versturen&quot;</em> en je facturen gaan onmiddellijk naar Billit zodra je ze op status &quot;Verzonden&quot; zet. Geen manuele stap meer.
          </div>
        </div>
      </details>
    </div>
  );
}
